import React, { useCallback, useMemo, useState, ReactNode } from "react"
import { GetApp } from "../../App"
import { EditGroup, EditGroupData } from "../../Components/EditGroup"
import { canonicalEnumUnit } from "../../App/Units"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { PredefinedSelect } from "../../Components/PredefinedSelect"
import {
  IShipSettings, IUnitSetting, IUnitDistance, IUnitSpeed, IUnitWind, IUnitCurrent,
  IUnitTemperature, IUnitPressure, IUnitLength, IUnitSurface, IUnitVolume, IUnitWeight,
  IUnitPower, IUnitCurrency,
} from "../../Types"
import { UnitSelectField } from "./UnitSelectField"

interface UnitsViewProps {
  settings: IShipSettings;
  edited: boolean;
  onEdited: () => void;
  onEditEvent: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  save: (data: FormData, values: EditGroupData) => Promise<void>;
}

// Units tab: a single EditGroup of unit selects. Each unit renders through the
// shared UnitSelectField; currency stays a PredefinedSelect special case.
export function UnitsView({ settings, edited, onEdited, onEditEvent, save }: UnitsViewProps) {
  const app = GetApp()!;
  const t = app.getT("ShipSettingsView");
  const namePrefix = `unit.`;

  const unit = useMemo((): IUnitSetting => {
    return settings?.unit || {} as IUnitSetting;
  }, [settings]);

  // Options are built from the enum's **values**, not its keys: the key is only the translation
  // key (`unit_<key>`). Emitting keys is what persisted the undecodable `hpa` in #1331.
  const unitOptions = useCallback((type: Record<string, string>): ReactNode[] => {
    return Object.entries(type).map(([key, value]) => {
      return <option value={value} key={key}>{t(`unit_${key}`)}</option>;
    });
  }, []);

  const unitDistanceOptions = useMemo(() => unitOptions(IUnitDistance), [unitOptions]);
  const unitSpeedOptions = useMemo(() => unitOptions(IUnitSpeed), [unitOptions]);
  const unitWindOptions = useMemo(() => unitOptions(IUnitWind), [unitOptions]);
  const unitCurrentOptions = useMemo(() => unitOptions(IUnitCurrent), [unitOptions]);
  const unitTemperaturOptions = useMemo(() => unitOptions(IUnitTemperature), [unitOptions]);
  const unitPressureOptions = useMemo(() => unitOptions(IUnitPressure), [unitOptions]);
  const unitLengthOptions = useMemo(() => unitOptions(IUnitLength), [unitOptions]);
  const unitSurfaceOptions = useMemo(() => unitOptions(IUnitSurface), [unitOptions]);
  const unitVolumeOptions = useMemo(() => unitOptions(IUnitVolume), [unitOptions]);
  const unitWeightOptions = useMemo(() => unitOptions(IUnitWeight), [unitOptions]);
  const unitPowerOptions = useMemo(() => unitOptions(IUnitPower), [unitOptions]);

  const [currency, setCurrency] = useState<string | undefined>(unit.currency);
  const predefinedCurrencies = useMemo(() => Object.keys(IUnitCurrency).filter((key) => typeof (key) === "string"), []);

  const selectCurrency = useCallback((value?: string) => {
    setCurrency(value);
    if (onEdited) {
      onEdited();
    }
  }, [onEdited]);

  return (
    <EditGroup changed={edited} editable={true} editableFields={[{ name: "unit", type: "object", fields: ["length", "weight", "speed", "temperature", "volume", "surface", "currency", "distance", "power", "wind", "pressure", "current"] }]} onSave={save}>
      <Block className="tab-content">
        <BlockRow>
          <UnitSelectField label={t("distance")} name={`${namePrefix}distance`} defaultValue={canonicalEnumUnit(unit.distance, IUnitDistance)} options={unitDistanceOptions} onChange={onEditEvent} />
          <UnitSelectField label={t("length")} name={`${namePrefix}length`} defaultValue={canonicalEnumUnit(unit.length, IUnitLength)} options={unitLengthOptions} onChange={onEditEvent} />
          <UnitSelectField label={t("surface")} name={`${namePrefix}surface`} defaultValue={canonicalEnumUnit(unit.surface, IUnitSurface)} options={unitSurfaceOptions} onChange={onEditEvent} />
          <UnitSelectField label={t("speed")} name={`${namePrefix}speed`} defaultValue={canonicalEnumUnit(unit.speed, IUnitSpeed)} options={unitSpeedOptions} onChange={onEditEvent} />
          <UnitSelectField label={t("wind")} name={`${namePrefix}wind`} defaultValue={canonicalEnumUnit(unit.wind, IUnitWind)} options={unitWindOptions} onChange={onEditEvent} />
          <UnitSelectField label={t("current")} name={`${namePrefix}current`} defaultValue={canonicalEnumUnit(unit.current, IUnitCurrent)} options={unitCurrentOptions} onChange={onEditEvent} />
        </BlockRow>

        <BlockRow className="distance_bottom">
          <UnitSelectField label={t("volume")} name={`${namePrefix}volume`} defaultValue={canonicalEnumUnit(unit.volume, IUnitVolume)} options={unitVolumeOptions} onChange={onEditEvent} />
          <UnitSelectField label={t("weight")} name={`${namePrefix}weight`} defaultValue={canonicalEnumUnit(unit.weight, IUnitWeight)} options={unitWeightOptions} onChange={onEditEvent} />
          <UnitSelectField label={t("temperature")} name={`${namePrefix}temperature`} defaultValue={canonicalEnumUnit(unit.temperature, IUnitTemperature)} options={unitTemperaturOptions} onChange={onEditEvent} />
          <UnitSelectField label={t("pressure")} name={`${namePrefix}pressure`} defaultValue={canonicalEnumUnit(unit.pressure, IUnitPressure)} options={unitPressureOptions} onChange={onEditEvent} />
          <UnitSelectField label={t("power")} name={`${namePrefix}power`} defaultValue={canonicalEnumUnit(unit.power, IUnitPower)} options={unitPowerOptions} onChange={onEditEvent} />
          <BlockField label={t("currency")} className="block_field_small_8">
            <PredefinedSelect value={currency} predefined={predefinedCurrencies} label={(v) => t(`unit_${v}`)} customValue={t("other")} name={`${namePrefix}currency`} onSelect={selectCurrency} />
          </BlockField>
        </BlockRow>
      </Block>
    </EditGroup>
  );
}
